// mock-only: the browser's one read of the analytics service (ADR-0012).
//
// Layer 2 (Engine): the effect that fetches an envelope lives here so the
// drawer components and their transform stay pure. Two calls, both GETs of
// committed material through the adapter — `loadFixtureResult` for the
// envelope and `loadFixtureSlices` for the knowledge that labels its roles.
// Evaluation is never requested from the glass: that call needs a full
// request carrying the raw observations, which the mock does not ship.

import { useEffect, useState } from 'react';

import {
  loadFixtureResult,
  loadFixtureSlices,
} from '@/shared/contracts/analytics/client';
import type { AnalysisRequest, AnalysisResult } from '@/shared/contracts/analytics/v1';

export type AnalysisRunState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; result: AnalysisResult; slices: AnalysisRequest['slices'] }
  | { status: 'failed' };

/** The three load states of one analysis run; `undefined` never fetches. */
export function useAnalysisRun(fixture: string | undefined): AnalysisRunState {
  const [state, setState] = useState<AnalysisRunState>(
    fixture ? { status: 'loading' } : { status: 'idle' },
  );

  useEffect(() => {
    if (!fixture) {
      setState({ status: 'idle' });
      return;
    }

    // The two loaders take no signal, so a resolution that lands after the
    // effect is torn down is dropped by this flag rather than aborted.
    let cancelled = false;
    setState({ status: 'loading' });

    void Promise.all([loadFixtureResult(fixture), loadFixtureSlices(fixture)])
      .then(([result, slices]) => {
        if (cancelled) return;
        setState({ status: 'ready', result, slices });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: 'failed' });
      });

    return () => {
      cancelled = true;
    };
  }, [fixture]);

  return state;
}
